import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Upload } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { FormError, Label } from '@/components/ui/field';
import { useClientImport } from '@/hooks/clients';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import {
  FRESH_WINDOWS,
  FRESH_WINDOW_LABELS,
  IMPORT_DESTINATIONS,
  IMPORT_DESTINATION_HINTS,
  IMPORT_DESTINATION_LABELS,
  SKIP_REASON_LABELS,
  type FreshWindow,
  type ImportPreview,
} from '@shared/schemas/client-import';

/**
 * Import the old website's contact list — owner request 2026-08-07,
 * **rewritten 2026-08-10.**
 *
 * **Nothing is written until the numbers are on the screen.** Choosing the file
 * runs a dry run; the office sees where every row would land, and only then is
 * there a button that writes anything. An import is easy to start and tedious to
 * undo, and the asymmetry is the whole argument for the extra click.
 *
 * **What changed, and why the screen looks different.** The first version asked
 * "who should be imported?" and offered three filters over a single outcome —
 * everything it took became a *customer*, and everything it did not take was
 * discarded. That framing hid the real question. This screen answers *where each
 * row goes* instead, and the only thing left to choose is how far back an
 * enquiry still counts as worth chasing.
 */
export function ImportClientsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          {/* Keyed, so reopening starts from an empty file picker rather than
              from the last preview somebody looked at. */}
          <ImportForm
            key={String(open)}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ImportForm({ onDone }: { onDone: () => void }) {
  const toast = useToast();
  const runImport = useClientImport();

  const [file, setFile] = useState<File | null>(null);
  const [freshDays, setFreshDays] = useState<FreshWindow>(90);
  const [preview, setPreview] = useState<ImportPreview | null>(null);

  const error = runImport.error instanceof ApiError ? runImport.error : null;

  function look(nextFile: File, nextFreshDays: FreshWindow) {
    runImport.mutate(
      { file: nextFile, freshDays: nextFreshDays, commit: false },
      {
        onSuccess: (outcome) => {
          setPreview(outcome.preview);
        },
      },
    );
  }

  function commit() {
    if (file === null) return;

    runImport.mutate(
      { file, freshDays, commit: true },
      {
        onSuccess: (outcome) => {
          const clients = outcome.result?.clientsCreated ?? 0;
          const leads = outcome.result?.leadsCreated ?? 0;

          toast.success(
            clients + leads === 0
              ? 'Nothing new to add — everyone in that file is already here.'
              : `${String(clients)} customer${clients === 1 ? '' : 's'} and ${String(leads)} lead${
                  leads === 1 ? '' : 's'
                } added.`,
          );
          onDone();
        },
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That import could not be run.');
        },
      },
    );
  }

  const skipped = preview?.rows.filter((row) => row.skipReason !== null) ?? [];
  const willCreate =
    preview === null
      ? 0
      : preview.countsByDestination.client +
        preview.countsByDestination.lead_new +
        preview.countsByDestination.lead_lost;

  return (
    <div className="space-y-5">
      <Dialog.Title className="text-h3 text-ink-900">Import contacts</Dialog.Title>
      <Dialog.Description className="text-meta text-ink-500">
        A CSV from another system. Nothing is saved until you have seen where every row would go.
      </Dialog.Description>

      {error ? <FormError>{error.message}</FormError> : null}

      <div className="space-y-1.5">
        <Label htmlFor="import-file">The file</Label>
        <input
          id="import-file"
          type="file"
          accept=".csv,text/csv"
          className="block w-full text-body file:mr-3 file:min-h-touch file:rounded-md file:border file:border-ink-300 file:bg-white file:px-3 file:text-label"
          onChange={(event) => {
            const chosen = event.target.files?.[0] ?? null;
            setFile(chosen);
            setPreview(null);
            if (chosen) look(chosen, freshDays);
          }}
        />
        <p className="text-meta text-ink-500">
          Column names are matched automatically — they do not have to match ours, and the order
          does not matter.
        </p>
      </div>

      {preview === null ? null : (
        <>
          {/*
            **Where everything goes, before anything is written.** Four rows
            rather than a single "will create" number, because the number was
            never the question — a file like this one is a customer list, a
            stale enquiry pile and a newsletter list in one, and the office
            needs to see that split before it agrees to it.
          */}
          <div className="space-y-2">
            <p className="text-label text-ink-900">Where these {preview.totalRows} rows go</p>
            {IMPORT_DESTINATIONS.map((destination) => (
              <div
                key={destination}
                className={cn(
                  'flex items-start gap-3 rounded-md border p-3',
                  destination === 'skipped'
                    ? 'border-ink-300 bg-ink-50'
                    : 'border-ink-300 bg-white',
                )}
              >
                <span className="min-w-[3rem] text-right text-h3 tabular-nums text-ink-900">
                  {preview.countsByDestination[destination]}
                </span>
                <span className="min-w-0">
                  <span className="block text-label text-ink-900">
                    {IMPORT_DESTINATION_LABELS[destination]}
                  </span>
                  <span className="block text-meta text-ink-500">
                    {IMPORT_DESTINATION_HINTS[destination]}
                  </span>
                </span>
              </div>
            ))}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-label text-ink-900">
              How recent does an enquiry have to be to count as new?
            </legend>
            <p className="text-meta text-ink-500">
              Anything older still comes in, marked Lost. Nothing is thrown away.
            </p>
            <div className="flex flex-wrap gap-2">
              {FRESH_WINDOWS.map((value) => (
                <label
                  key={value}
                  className={cn(
                    'flex min-h-touch cursor-pointer items-center gap-2 rounded-md border px-3',
                    freshDays === value
                      ? 'border-honey-400 bg-honey-50'
                      : 'border-ink-300 bg-white',
                  )}
                >
                  <input
                    type="radio"
                    name="freshDays"
                    className="size-4"
                    checked={freshDays === value}
                    onChange={() => {
                      setFreshDays(value);
                      if (file) look(file, value);
                    }}
                  />
                  <span className="text-label text-ink-900">{FRESH_WINDOW_LABELS[value]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {preview.mergedRows > 0 ? (
            <p className="text-meta text-ink-500">
              {preview.mergedRows} row{preview.mergedRows === 1 ? '' : 's'} shared an email address
              with another and {preview.mergedRows === 1 ? 'was' : 'were'} folded together, keeping
              the fullest version of each.
            </p>
          ) : null}

          {/*
            **The one fact in the file that cannot be recovered later** — owner
            decision, 2026-08-10. There is no newsletter in this system yet, so
            this says nothing about what will be sent; it says the agreement is
            being kept rather than dropped with the row that carried it.
          */}
          {preview.marketingOptIns > 0 ? (
            <p className="rounded-md border border-ink-300 bg-white p-3 text-meta text-ink-700">
              <span className="tabular-nums text-ink-900">{preview.marketingOptIns}</span> of them
              agreed to receive email in the old system. That is recorded against each one, so they
              can be contacted if a newsletter is ever set up. Nothing is sent to anybody now.
            </p>
          ) : null}

          {/*
            **Said out loud rather than discovered later.** An import that
            silently produces customers with no address looks like it failed
            halfway; saying the file has none makes it a fact about the file.
          */}
          {preview.hasNoAddresses ? (
            <p className="flex items-start gap-2 rounded-md border border-ink-300 bg-honey-50 p-3 text-meta text-bark-900">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              This file has no addresses in it, so nobody will have a property. You can add one to
              each customer later, and a job cannot be booked until you do.
            </p>
          ) : null}

          {skipped.length > 0 ? (
            <details className="rounded-md border border-ink-300 bg-white p-3">
              <summary className="cursor-pointer text-label text-ink-900">
                What is being skipped, and why
              </summary>
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                {skipped.slice(0, 200).map((row) => (
                  <li key={row.line} className="flex flex-wrap gap-x-2 text-meta text-ink-500">
                    <span className="tabular-nums">Row {row.line}</span>
                    <span className="text-ink-700">{row.name || row.email || '(blank)'}</span>
                    <span>— {row.skipReason ? SKIP_REASON_LABELS[row.skipReason] : ''}</span>
                  </li>
                ))}
              </ul>
              {skipped.length > 200 ? (
                <p className="mt-2 text-meta text-ink-500">…and {skipped.length - 200} more.</p>
              ) : null}
            </details>
          ) : null}
        </>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={preview === null || willCreate === 0 || runImport.isPending}
          onClick={commit}
        >
          <Upload aria-hidden="true" />
          {runImport.isPending
            ? 'Working…'
            : preview === null
              ? 'Import'
              : `Import ${String(willCreate)}`}
        </Button>
      </div>
    </div>
  );
}
